import React, { useState, useEffect, useCallback } from "react";
import {
  MapPin, Phone, Globe, Star, Plus, Edit2, Trash2, Save,
  X, Award, Search, ExternalLink, RefreshCw, Eye, EyeOff
} from "lucide-react";

interface VisitorListing {
  id: string;
  type: string;
  name: string;
  description: string;
  address: string;
  area: string;
  phone: string;
  email: string;
  website: string;
  bookingLink: string;
  imageUrl: string;
  priceRange: string;
  rating: number;
  isVerified: boolean;
  isFeatured: boolean;
  isActive: boolean;
  amenities: string;
  openingHours: string;
}

const TYPES = [
  { id:"hotel",        label:"Hotels & Resorts",     icon:"🏨", color:"#C9A84C" },
  { id:"restaurant",   label:"Restaurants & Dining",  icon:"🍽️", color:"#fb923c" },
  { id:"tourist_site", label:"Tourist Sites",          icon:"📸", color:"#a78bfa" },
  { id:"monument",     label:"Monuments & Heritage",   icon:"🏛️", color:"#2dd4bf" },
  { id:"market",       label:"Markets & Shopping",     icon:"🛒", color:"#4ade80" },
  { id:"club",         label:"Clubs & Entertainment",  icon:"🎵", color:"#f472b6" },
  { id:"transport",    label:"Cab & Taxi Services",    icon:"🚗", color:"#60a5fa" },
  { id:"other",        label:"Other Places",           icon:"📍", color:"#94a3b8" },
];

const EMPTY: Omit<VisitorListing,"id"> = {
  type:"hotel", name:"", description:"", address:"", area:"", phone:"",
  email:"", website:"", bookingLink:"", imageUrl:"", priceRange:"mid",
  rating:0, isVerified:false, isFeatured:false, isActive:true,
  amenities:"[]", openingHours:""
};

function toast(msg: string, ok = true) {
  const el = document.createElement("div");
  el.textContent = msg;
  el.style.cssText = `position:fixed;bottom:24px;right:24px;z-index:9999;padding:12px 20px;border-radius:8px;font-size:14px;font-weight:600;color:#EDE8DC;background:${ok?"#0F5132":"#7f1d1d"};border:1px solid ${ok?"#C9A84C":"#ef4444"};box-shadow:0 4px 20px rgba(0,0,0,0.5)`;
  document.body.appendChild(el);
  setTimeout(()=>{el.style.opacity="0";el.style.transition="opacity 0.4s";setTimeout(()=>el.remove(),400);},3000);
}

export function VisitorGuideAdmin({ currentUser }: { currentUser?: any; key?: React.Key }) {
  const [listings, setListings] = useState<VisitorListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [searchQ, setSearchQ] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<VisitorListing|null>(null);
  const [form, setForm] = useState<Omit<VisitorListing,"id">>(EMPTY);
  const [deleteConfirm, setDeleteConfirm] = useState<VisitorListing|null>(null);
  const [amenitiesInput, setAmenitiesInput] = useState("");
  const [activeTab, setActiveTab] = useState<"listings"|"hotel-regs">("listings");
  const [hotelRegs, setHotelRegs] = useState<any[]>([]);

  const token = localStorage.getItem("erue_auth_token") || sessionStorage.getItem("erue_auth_token") || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/visitor-listings");
      const d = await r.json();
      setListings(Array.isArray(d) ? d : []);
    } catch { setListings([]); }
    finally { setLoading(false); }
  }, []);

  const loadHotelRegs = useCallback(async () => {
    try {
      const r = await fetch("/api/hotel-registrations", {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const d = await r.json();
      setHotelRegs(Array.isArray(d) ? d : []);
    } catch { setHotelRegs([]); }
  }, [token]);

  useEffect(() => { load(); loadHotelRegs(); }, [load, loadHotelRegs]);

  const openAdd = () => {
    setEditItem(null);
    setForm({...EMPTY, type: filterType !== "all" ? filterType : "hotel"});
    setAmenitiesInput(""); setShowModal(true);
  };

  const openEdit = (item: VisitorListing) => {
    setEditItem(item);
    setForm({ type:item.type, name:item.name, description:item.description,
      address:item.address, area:item.area, phone:item.phone, email:item.email,
      website:item.website, bookingLink:item.bookingLink, imageUrl:item.imageUrl,
      priceRange:item.priceRange, rating:item.rating, isVerified:item.isVerified,
      isFeatured:item.isFeatured, isActive:item.isActive,
      amenities:item.amenities, openingHours:item.openingHours });
    try { setAmenitiesInput(JSON.parse(item.amenities||"[]").join(", ")); } catch { setAmenitiesInput(""); }
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast("Name is required", false); return; }
    const amenitiesArr = amenitiesInput.split(",").map(s=>s.trim()).filter(Boolean);
    const payload = { ...form, amenities: JSON.stringify(amenitiesArr) };
    try {
      const url = editItem ? `/api/visitor-listings/${editItem.id}` : "/api/visitor-listings";
      const method = editItem ? "PUT" : "POST";
      const r = await fetch(url, {
        method, headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`},
        body: JSON.stringify(payload)
      });
      if (!r.ok) throw new Error("Save failed");
      toast(editItem ? "Listing updated" : "Listing added");
      setShowModal(false); await load();
    } catch(e:any) { toast(e.message||"Save failed", false); }
  };

  const doDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await fetch(`/api/visitor-listings/${deleteConfirm.id}`, {
        method:"DELETE", headers:{"Authorization":`Bearer ${token}`}
      });
      toast("Deleted"); setDeleteConfirm(null); await load();
    } catch { toast("Delete failed", false); }
  };

  const toggleField = async (item: VisitorListing, field: "isActive"|"isFeatured") => {
    try {
      await fetch(`/api/visitor-listings/${item.id}`, {
        method:"PUT",
        headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`},
        body: JSON.stringify({ [field]: !item[field] })
      });
      await load();
    } catch { toast("Update failed", false); }
  };

  const reviewHotelReg = async (id: string, status: string) => {
    try {
      await fetch(`/api/hotel-registrations/${id}`, {
        method:"PUT",
        headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`},
        body: JSON.stringify({ status })
      });
      toast(status === "approved" ? "Hotel approved + added to listings" : "Hotel rejected");
      loadHotelRegs(); load();
    } catch { toast("Update failed", false); }
  };

  const filtered = listings.filter(l => {
    const matchType = filterType === "all" || l.type === filterType;
    const matchQ = !searchQ || l.name.toLowerCase().includes(searchQ.toLowerCase()) || l.area.toLowerCase().includes(searchQ.toLowerCase());
    return matchType && matchQ;
  });

  const getType = (id:string) => TYPES.find(t=>t.id===id) || TYPES[TYPES.length-1];

  const IS: React.CSSProperties = {
    width:"100%",padding:"9px 12px",background:"#121E16",
    border:"1px solid #1C2E22",borderRadius:8,color:"#EDE8DC",
    fontSize:14,outline:"none",boxSizing:"border-box"
  };

  return (
    <div style={{background:"#070D0A",minHeight:"100vh",padding:"24px",fontFamily:"Plus Jakarta Sans, sans-serif"}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12,marginBottom:20}}>
        <div>
          <h1 style={{fontFamily:"Cinzel,serif",color:"#C9A84C",fontSize:22,margin:0}}>Visitor Guide Admin</h1>
          <p style={{color:"rgba(237,232,220,0.5)",fontSize:13,margin:"4px 0 0"}}>
            Hotels, dining, attractions, transport & more — {listings.length} listings
          </p>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={load} style={{padding:"8px 14px",background:"#0E1812",border:"1px solid #1C2E22",borderRadius:8,color:"rgba(237,232,220,0.6)",cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",gap:6}}>
            <RefreshCw size={14}/> Refresh
          </button>
          <button onClick={openAdd} style={{padding:"10px 20px",background:"#C9A84C",border:"none",borderRadius:8,color:"#070D0A",fontWeight:700,cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",gap:8}}>
            <Plus size={16}/> Add Listing
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:4,marginBottom:20,background:"#0E1812",borderRadius:10,padding:4,border:"1px solid #1C2E22",maxWidth:400}}>
        <button onClick={()=>setActiveTab("listings")} style={{flex:1,padding:"8px",borderRadius:8,border:"none",cursor:"pointer",fontSize:13,fontWeight:600,background:activeTab==="listings"?"#0F5132":"transparent",color:activeTab==="listings"?"#C9A84C":"rgba(237,232,220,0.5)"}}>
          Listings ({listings.length})
        </button>
        <button onClick={()=>setActiveTab("hotel-regs")} style={{flex:1,padding:"8px",borderRadius:8,border:"none",cursor:"pointer",fontSize:13,fontWeight:600,background:activeTab==="hotel-regs"?"#0F5132":"transparent",color:activeTab==="hotel-regs"?"#C9A84C":"rgba(237,232,220,0.5)"}}>
          Hotel Registrations ({hotelRegs.filter(r=>r.status==="pending").length} pending)
        </button>
      </div>

      {activeTab === "listings" && (
        <>
          {/* Type Filter */}
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>
            <button onClick={()=>setFilterType("all")} style={{padding:"6px 14px",borderRadius:8,border:`1px solid ${filterType==="all"?"#C9A84C":"#1C2E22"}`,background:filterType==="all"?"#0F5132":"#0E1812",color:filterType==="all"?"#C9A84C":"rgba(237,232,220,0.5)",cursor:"pointer",fontSize:12,fontWeight:600}}>
              All ({listings.length})
            </button>
            {TYPES.map(t => {
              const count = listings.filter(l=>l.type===t.id).length;
              return (
                <button key={t.id} onClick={()=>setFilterType(t.id)} style={{padding:"6px 12px",borderRadius:8,border:`1px solid ${filterType===t.id?t.color:"#1C2E22"}`,background:filterType===t.id?t.color+"22":"#0E1812",color:filterType===t.id?t.color:"rgba(237,232,220,0.5)",cursor:"pointer",fontSize:12,fontWeight:600}}>
                  {t.icon} {t.label.split(" ")[0]} ({count})
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div style={{position:"relative",marginBottom:16,maxWidth:380}}>
            <Search size={14} style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:"rgba(237,232,220,0.4)"}}/>
            <input value={searchQ} onChange={e=>setSearchQ(e.target.value)} placeholder="Search listings..."
              style={{...IS,paddingLeft:34}}/>
          </div>

          {loading ? (
            <div style={{textAlign:"center",padding:60,color:"rgba(237,232,220,0.4)"}}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={{textAlign:"center",padding:60,color:"rgba(237,232,220,0.4)"}}>
              <MapPin size={40} style={{margin:"0 auto 16px",opacity:0.3,display:"block"}}/>
              <p>No listings yet. Click "Add Listing" to get started.</p>
            </div>
          ) : (
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:12}}>
              {filtered.map(item => {
                const t = getType(item.type);
                return (
                  <div key={item.id} style={{background:"#0E1812",border:"1px solid #1C2E22",borderRadius:12,padding:16,borderLeft:`3px solid ${t.color}`,opacity:item.isActive?1:0.6}}>
                    <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8,marginBottom:8}}>
                      <div style={{flex:1}}>
                        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4,flexWrap:"wrap"}}>
                          <span style={{fontSize:16}}>{t.icon}</span>
                          <span style={{color:"#EDE8DC",fontSize:14,fontWeight:700}}>{item.name}</span>
                          {item.isFeatured && <Award size={13} style={{color:"#C9A84C"}}/>}
                          {!item.isActive && <span style={{color:"#ef4444",fontSize:10,padding:"1px 5px",border:"1px solid #ef4444",borderRadius:4}}>INACTIVE</span>}
                        </div>
                        <span style={{padding:"2px 8px",borderRadius:10,fontSize:11,fontWeight:600,background:t.color+"22",color:t.color}}>{t.label}</span>
                      </div>
                      <div style={{display:"flex",gap:3,flexShrink:0}}>
                        <button onClick={()=>toggleField(item,"isFeatured")} title="Toggle featured" style={{padding:"4px",background:"transparent",border:"1px solid #1C2E22",borderRadius:6,color:item.isFeatured?"#C9A84C":"rgba(237,232,220,0.3)",cursor:"pointer"}}>
                          <Star size={12}/>
                        </button>
                        <button onClick={()=>toggleField(item,"isActive")} title="Toggle active" style={{padding:"4px",background:"transparent",border:"1px solid #1C2E22",borderRadius:6,color:item.isActive?"#4ade80":"#ef4444",cursor:"pointer"}}>
                          {item.isActive?<Eye size={12}/>:<EyeOff size={12}/>}
                        </button>
                        <button onClick={()=>openEdit(item)} style={{padding:"4px 7px",background:"transparent",border:"1px solid #1C2E22",borderRadius:6,color:"#60a5fa",cursor:"pointer",fontSize:11,display:"flex",alignItems:"center",gap:3}}>
                          <Edit2 size={11}/> Edit
                        </button>
                        <button onClick={()=>setDeleteConfirm(item)} style={{padding:"4px 7px",background:"transparent",border:"1px solid #1C2E22",borderRadius:6,color:"#ef4444",cursor:"pointer",fontSize:11,display:"flex",alignItems:"center",gap:3}}>
                          <Trash2 size={11}/> Del
                        </button>
                      </div>
                    </div>
                    {item.area && <p style={{color:"rgba(237,232,220,0.5)",fontSize:12,margin:"4px 0",display:"flex",alignItems:"center",gap:4}}><MapPin size={10}/>{item.area}</p>}
                    {item.phone && <p style={{color:"rgba(237,232,220,0.5)",fontSize:12,margin:"2px 0",display:"flex",alignItems:"center",gap:4}}><Phone size={10}/>{item.phone}</p>}
                    {item.description && <p style={{color:"rgba(237,232,220,0.4)",fontSize:12,margin:"6px 0 0",lineHeight:1.5,overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical" as any}}>{item.description}</p>}
                    {item.bookingLink && (
                      <a href={item.bookingLink} target="_blank" rel="noopener noreferrer"
                        style={{display:"inline-flex",alignItems:"center",gap:4,marginTop:8,color:"#C9A84C",fontSize:12,textDecoration:"none"}}>
                        <ExternalLink size={11}/> Book
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {activeTab === "hotel-regs" && (
        <div>
          <p style={{color:"rgba(237,232,220,0.5)",fontSize:13,marginBottom:16}}>
            Hotels that self-registered. Approve to add them to the Visitor Guide.
          </p>
          {hotelRegs.length === 0 ? (
            <div style={{textAlign:"center",padding:60,color:"rgba(237,232,220,0.4)"}}>No hotel registrations yet.</div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {hotelRegs.map(reg => (
                <div key={reg.id} style={{background:"#0E1812",border:"1px solid #1C2E22",borderRadius:10,padding:"14px 18px"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
                    <div>
                      <span style={{color:"#EDE8DC",fontSize:15,fontWeight:700}}>{reg.name}</span>
                      <span style={{marginLeft:10,padding:"2px 8px",borderRadius:10,fontSize:11,background:reg.status==="pending"?"#1a1a00":reg.status==="approved"?"#0F5132":"#7f1d1d",color:reg.status==="pending"?"#fbbf24":reg.status==="approved"?"#C9A84C":"#ef4444"}}>
                        {reg.status.toUpperCase()}
                      </span>
                    </div>
                    {reg.status === "pending" && (
                      <div style={{display:"flex",gap:8}}>
                        <button onClick={()=>reviewHotelReg(reg.id,"approved")} style={{padding:"6px 14px",background:"#0F5132",border:"1px solid #C9A84C",borderRadius:6,color:"#C9A84C",cursor:"pointer",fontSize:12,fontWeight:600}}>
                          ✓ Approve
                        </button>
                        <button onClick={()=>reviewHotelReg(reg.id,"rejected")} style={{padding:"6px 14px",background:"transparent",border:"1px solid #ef4444",borderRadius:6,color:"#ef4444",cursor:"pointer",fontSize:12}}>
                          ✕ Reject
                        </button>
                      </div>
                    )}
                  </div>
                  <div style={{display:"flex",gap:16,flexWrap:"wrap",marginTop:6}}>
                    <span style={{color:"rgba(237,232,220,0.5)",fontSize:12}}>{reg.area}</span>
                    <span style={{color:"rgba(237,232,220,0.5)",fontSize:12}}>{reg.email}</span>
                    <span style={{color:"rgba(237,232,220,0.5)",fontSize:12}}>{reg.phone}</span>
                    <span style={{color:"rgba(237,232,220,0.5)",fontSize:12}}>{reg.priceRange}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setShowModal(false)}>
          <div style={{background:"#0E1812",border:"1px solid #C9A84C",borderRadius:16,padding:28,width:"100%",maxWidth:620,maxHeight:"90vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <h3 style={{color:"#C9A84C",fontFamily:"Cinzel,serif",margin:0,fontSize:17}}>{editItem?"Edit Listing":"Add Listing"}</h3>
              <button onClick={()=>setShowModal(false)} style={{background:"none",border:"none",color:"rgba(237,232,220,0.5)",cursor:"pointer"}}><X size={20}/></button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{display:"block",color:"rgba(237,232,220,0.6)",fontSize:11,fontWeight:600,marginBottom:5,textTransform:"uppercase"}}>Type *</label>
                <select value={form.type} onChange={e=>setForm({...form,type:e.target.value})} style={IS}>
                  {TYPES.map(t=><option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
                </select>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{display:"block",color:"rgba(237,232,220,0.6)",fontSize:11,fontWeight:600,marginBottom:5,textTransform:"uppercase"}}>Name *</label>
                <input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="e.g. Grand Palace Hotel" style={IS}/>
              </div>
              <div>
                <label style={{display:"block",color:"rgba(237,232,220,0.6)",fontSize:11,fontWeight:600,marginBottom:5,textTransform:"uppercase"}}>Area / Location</label>
                <input value={form.area} onChange={e=>setForm({...form,area:e.target.value})} placeholder="e.g. Jattu-Uzairue" style={IS}/>
              </div>
              <div>
                <label style={{display:"block",color:"rgba(237,232,220,0.6)",fontSize:11,fontWeight:600,marginBottom:5,textTransform:"uppercase"}}>Price Range</label>
                <select value={form.priceRange} onChange={e=>setForm({...form,priceRange:e.target.value})} style={IS}>
                  <option value="">-- Select --</option>
                  <option value="budget">Budget</option>
                  <option value="mid">Mid-Range</option>
                  <option value="luxury">Luxury</option>
                </select>
              </div>
              <div>
                <label style={{display:"block",color:"rgba(237,232,220,0.6)",fontSize:11,fontWeight:600,marginBottom:5,textTransform:"uppercase"}}>Phone</label>
                <input value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} placeholder="+234..." style={IS}/>
              </div>
              <div>
                <label style={{display:"block",color:"rgba(237,232,220,0.6)",fontSize:11,fontWeight:600,marginBottom:5,textTransform:"uppercase"}}>Email</label>
                <input value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="contact@..." style={IS}/>
              </div>
              <div>
                <label style={{display:"block",color:"rgba(237,232,220,0.6)",fontSize:11,fontWeight:600,marginBottom:5,textTransform:"uppercase"}}>Website</label>
                <input value={form.website} onChange={e=>setForm({...form,website:e.target.value})} placeholder="https://..." style={IS}/>
              </div>
              <div>
                <label style={{display:"block",color:"rgba(237,232,220,0.6)",fontSize:11,fontWeight:600,marginBottom:5,textTransform:"uppercase"}}>Booking Link</label>
                <input value={form.bookingLink} onChange={e=>setForm({...form,bookingLink:e.target.value})} placeholder="https://booking.com/..." style={IS}/>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{display:"block",color:"rgba(237,232,220,0.6)",fontSize:11,fontWeight:600,marginBottom:5,textTransform:"uppercase"}}>Full Address</label>
                <input value={form.address} onChange={e=>setForm({...form,address:e.target.value})} placeholder="Street address" style={IS}/>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{display:"block",color:"rgba(237,232,220,0.6)",fontSize:11,fontWeight:600,marginBottom:5,textTransform:"uppercase"}}>Opening Hours</label>
                <input value={form.openingHours} onChange={e=>setForm({...form,openingHours:e.target.value})} placeholder="Mon-Sun: 07:00-22:00" style={IS}/>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{display:"block",color:"rgba(237,232,220,0.6)",fontSize:11,fontWeight:600,marginBottom:5,textTransform:"uppercase"}}>Amenities (comma separated)</label>
                <input value={amenitiesInput} onChange={e=>setAmenitiesInput(e.target.value)} placeholder="WiFi, Pool, Parking, AC..." style={IS}/>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{display:"block",color:"rgba(237,232,220,0.6)",fontSize:11,fontWeight:600,marginBottom:5,textTransform:"uppercase"}}>Image URL</label>
                <input value={form.imageUrl} onChange={e=>setForm({...form,imageUrl:e.target.value})} placeholder="https://res.cloudinary.com/..." style={IS}/>
              </div>
              <div style={{gridColumn:"1/-1"}}>
                <label style={{display:"block",color:"rgba(237,232,220,0.6)",fontSize:11,fontWeight:600,marginBottom:5,textTransform:"uppercase"}}>Description</label>
                <textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})} rows={3} style={{...IS,resize:"vertical"}}/>
              </div>
              <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <input type="checkbox" checked={form.isActive} onChange={e=>setForm({...form,isActive:e.target.checked})} style={{accentColor:"#C9A84C",width:16,height:16}}/>
                <span style={{color:"#EDE8DC",fontSize:14}}>Active (public)</span>
              </label>
              <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <input type="checkbox" checked={form.isFeatured} onChange={e=>setForm({...form,isFeatured:e.target.checked})} style={{accentColor:"#C9A84C",width:16,height:16}}/>
                <span style={{color:"#EDE8DC",fontSize:14}}>⭐ Featured</span>
              </label>
              <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <input type="checkbox" checked={form.isVerified} onChange={e=>setForm({...form,isVerified:e.target.checked})} style={{accentColor:"#C9A84C",width:16,height:16}}/>
                <span style={{color:"#EDE8DC",fontSize:14}}>✓ Verified</span>
              </label>
            </div>
            <div style={{display:"flex",gap:10,marginTop:24,justifyContent:"flex-end"}}>
              <button onClick={()=>setShowModal(false)} style={{padding:"10px 20px",background:"transparent",border:"1px solid #1C2E22",borderRadius:8,color:"rgba(237,232,220,0.7)",cursor:"pointer"}}>Cancel</button>
              <button onClick={save} style={{padding:"10px 24px",background:"#C9A84C",border:"none",borderRadius:8,color:"#070D0A",fontWeight:700,cursor:"pointer",fontSize:14}}>
                {editItem?"Update":"Add Listing"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:"#0E1812",border:"1px solid #ef4444",borderRadius:16,padding:28,maxWidth:380,width:"100%"}}>
            <h3 style={{color:"#ef4444",margin:"0 0 12px",fontFamily:"Cinzel,serif"}}>Delete Listing?</h3>
            <p style={{color:"rgba(237,232,220,0.7)",fontSize:14,margin:"0 0 20px"}}>Delete <strong style={{color:"#EDE8DC"}}>{deleteConfirm.name}</strong>? This cannot be undone.</p>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={()=>setDeleteConfirm(null)} style={{padding:"10px 20px",background:"transparent",border:"1px solid #1C2E22",borderRadius:8,color:"rgba(237,232,220,0.7)",cursor:"pointer"}}>Cancel</button>
              <button onClick={doDelete} style={{padding:"10px 20px",background:"#7f1d1d",border:"1px solid #ef4444",borderRadius:8,color:"#ef4444",fontWeight:700,cursor:"pointer"}}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VisitorGuideAdmin;
